import { Intent } from "@blueprintjs/core";
import { NotificationToaster, WizardModal } from "components/notifications";
import { UserHasAccess } from "components/user-access";
import { CompleteSignoff, ViewDocument } from "pages/policy/document/signoff/components";
import React, { useState } from "react";
import { EmployeeService } from "services/EmployeeService";
import { convertBase64ToBlob } from "utils/convertBase64ToBlob";
import PropTypes from 'prop-types';
import { PolicyDocumentTypes } from "pages/policy/document/constants/PolicyDocumentTypes";

export function SignoffDocumentModal({
    isOpen,
    onClose,
    onSignoffComplete,

    policySignoffActionItem = {
        policyId: null,
        documentId: null,
        employeeId: null,
        documentName: "",
        documentStorageIdentifier: "",
        completedDate: null,
    }
}) {
    policySignoffActionItem = policySignoffActionItem || {
        policyId: null,
        documentId: null,
        employeeId: null,
        documentName: "",
        documentStorageIdentifier: null,
        completedDate: null
    };

    const [documentLoaded, setDocumentLoaded] = useState(false);
    const [signatureFormLoaded, setSignatureFormLoaded] = useState(false);
    const [signature, setSignature] = useState("");
    const [wizardStep, setWizardStep] = useState(0);
    const [saving, setSaving] = useState(false);
    const [privacyPolicyRead, setPrivacyPolicyRead] = useState(false);
    const performAction = "Documents:PeformPolicySignOff";
    const userHasPerformAction = UserHasAccess(performAction);

    const steps = [
        {
            isValid: () => documentLoaded && !saving,
        },
        {
            isValid: () => signatureFormLoaded && !saving && signature.length > 0 && privacyPolicyRead,
        },
    ];

    const isValid = steps[wizardStep]?.isValid();

    const completeSignoff = () => {
        return new Promise((resolve, reject) => {
            if (!userHasPerformAction) {
                NotificationToaster.show(Intent.DANGER, "You do not have permission to signoff a document.");
                reject("Permission denied");
                return;
            }

            if (signature) {
                setSaving(true);
                EmployeeService.signoffPolicyDocument(policySignoffActionItem.policyId, policySignoffActionItem.documentId, policySignoffActionItem.employeeId, convertBase64ToBlob(signature))
                    .then(() => {
                        NotificationToaster.show(Intent.SUCCESS, "Policy document signed off successfully.");
                        onSignoffComplete();
                        resolve();
                    })
                    .catch((error) => {
                        NotificationToaster.show(Intent.DANGER, "The policy document could not be signed off. Please try again.");
                        reject(error);
                    })
                    .finally(() => {
                        setSaving(false);
                    });
            } else {
                NotificationToaster.show(Intent.WARNING, "Please provide a signature before completing the sign-off.");
                reject("No signature provided");
            }
        });
    };

    const isDisabled = !!policySignoffActionItem?.completedDate;

    const handlePrivacyPolicyRead = (isRead) => {
        setPrivacyPolicyRead(isRead);
    };

    const handleDocumentLoading = (isLoading) => {
        setDocumentLoaded(!isLoading);
    };

    const handleSignatureChange = (newSignature) => {
        setSignature(newSignature);
    };

    const handleSignatureFormLoading = (isLoading) => {
        setSignatureFormLoaded(!isLoading);
    };

    const navigateToStep = (stepIndex) => {
        setWizardStep(stepIndex);
    };

    return (
        <WizardModal
            id="document-signoff-wizard-modal"
            isOpen={isOpen}
            title="Document Sign-off"
            onStepChange={(stepIndex) => navigateToStep(stepIndex)}
            onFinish={completeSignoff}
            canProceed={isValid || saving || isDisabled}
            isDirty={false}
            disabled={saving}
            onCancel={onClose}
            resetOnCancel={true}
            allowBack={true}
            canFinish={true}>
            <div key="step-view-document" className="spacer-bottom-medium">
                <ViewDocument
                    documentName={policySignoffActionItem.documentName}
                    documentStorageIdentifier={policySignoffActionItem.documentStorageIdentifier}
                    onLoading={handleDocumentLoading}
                    documentType={PolicyDocumentTypes.UNSIGNED}
                />
            </div>
            <div key="step-view-signoff" className="spacer-bottom-medium">
                <CompleteSignoff
                    onLoading={handleSignatureFormLoading}
                    onSignatureChange={handleSignatureChange}
                    onPrivacyPolicyRead={handlePrivacyPolicyRead}
                    saving={saving}
                />
            </div>
        </WizardModal>
    );
}

SignoffDocumentModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSignoffComplete: PropTypes.func.isRequired,
    policySignoffActionItem: PropTypes.shape({
        policyId: PropTypes.number,
        documentId: PropTypes.number,
        employeeId: PropTypes.number,
        documentName: PropTypes.string,
        documentStorageIdentifier: PropTypes.string,
        completedDate: PropTypes.string,
    }),
};